import { WorkCase } from '@/data/types';

const novatimepuncher: WorkCase = {
  id: "novatimepuncher",
  slug: "novatimepuncher",
  title: "NT Puncher: Stempeluhr für Mac & iPhone",
  description: "Native App für Mac und iPhone zum Ein- und Ausstempeln in der NovaTime-Zeiterfassung, mit SwiftUI, Glass-UI und animierten SF Symbols. Dahinter steckt die spannendere Geschichte: die bit-genaue Ablösung meines eigenen Flutter-Vorgängers, KI-nativ gebaut.",
  tags: ["Swift", "SwiftUI", "macOS", "Swift Concurrency", "CryptoKit", "Flutter", "Migration"],
  image: "/assets/img/work/novatimepuncher.webp",
  articleType: "APP",
  date: "2025-09",
  category: "app",
  platforms: ["macOS", "iOS"],
  applicationCategory: "BusinessApplication",
  appStoreUrl: "https://apps.apple.com/de/app/nt-puncher/id6794961683",
  detailContent: {
    subtitle: "Ein-, aus- und Pause stempeln, als native App auf Mac und iPhone mit Glass-UI, animierten SF Symbols und Actor-Architektur. Und unter der Haube ein kompletter Stack-Wechsel, den niemand bemerken sollte.",
    challenge: [
      {
        text: "NT Puncher (in der App als Novatime Puncher ausgeschrieben) ist ein Freizeitprojekt in zwei Akten. Akt eins: eine kleine Flutter-Desktop-App fürs Ein- und Ausstempeln gegen die NovaTime-Zeiterfassung, mit Punch-In/Out, Pausen-Tracking und Auto-Logout. Geschrieben lange vor meinem KI-nativen Workflow, komplett von Hand. Sie lief jahrelang produktiv, aber sie fühlte sich auf dem Mac nie richtig zu Hause: generisches UI, schwergewichtige Runtime, und mit Swifts moderner Concurrency-Welt hatte der alte Singleton-und-Streams-Code nichts zu tun."
      },
      {
        text: "Akt zwei, wieder in der Freizeit: die App nativ in SwiftUI neu bauen, diesmal KI-nativ. Der Haken: Sie ist im produktiven Einsatz, und wenn eine Migration etwas Vertrautes bricht, erzeugt das Rückfragen und Misstrauen. Die Messlatte war deshalb zweigeteilt: Verhalten, Daten und Farblogik exakt gleich, während Optik und Bediengefühl endlich richtig macOS werden durften."
      },
      {
        text: "Zwei Paritäts-Anforderungen waren dabei hart, der Rest Produktentscheidung:"
      },
      {
        listing: [
          "Bestehende, verschlüsselt gespeicherte Zugangsdaten müssen transparent weitergelesen werden können, das Verschlüsselungsformat der Flutter-App ist damit bit-genau gesetzt",
          "Die Farblogik der Zeitanzeigen muss identisch bleiben, denn eine abweichende Tagesfarbe ist für die Nutzerschaft ein Bug",
          "Alles andere, von Texten über Abstände bis zum Auto-Logout-Verhalten, bleibt aus Prinzip 1:1, damit der Umstieg unsichtbar ist"
        ]
      }
    ],
    solution: [
      {
        text: "Der Kern der Umsetzung ist weniger ein UI-Rewrite als eine Methodik: Die alte App wird zur unantastbaren Referenz, und jede Abweichung braucht einen Grund. Vier Bausteine tragen das."
      },
      {
        subheadline: "Blueprint statt Bauchgefühl",
        text: "Die komplette Flutter-App liegt als Read-only-Referenz im Repo. Sie wird nie editiert, sondern liefert die Sollwerte: Business-Regeln, Zustandsübergänge, Texte, Farbberechnungen. Eine Migrations-Spec beschreibt das Soll im Detail, und Vergleiche gegen den Blueprint ersetzen Diskussionen. Die Zustandsmaschine des Stempelns (vier Zustände, vier Action-Codes, inklusive der Randfälle wie „Ausstempeln während der Pause“) wurde eins zu eins übernommen statt neu erfunden."
      },
      {
        subheadline: "Bit-genaue Krypto-Kompatibilität",
        text: "Die Flutter-App speichert Zugangsdaten AES-GCM-verschlüsselt in einem eigenen Format: Nonce, Ciphertext und Auth-Tag als Hex-Strings, per Schrägstrich getrennt. Die Swift-Seite implementiert exakt dieses Format mit CryptoKit: Bestandsdaten werden transparent entschlüsselt, neue kompatibel geschrieben. Es ist die einzige Stelle im Projekt, an der Bit-Genauigkeit gegenüber einem Fremdformat garantiert sein muss, und damit die Stelle, an der ein stiller Fehler am teuersten wäre."
      },
      {
        subheadline: "Actors statt Locks",
        text: "Die neue App nutzt Swifts Approachable Concurrency konsequent: UI-Code ist per Default MainActor-isoliert, und alles Nebenläufige (API-Controller, State-Service, Logging, Auto-Logout-Scheduler, Notifications) ist ein eigener Actor. Kein einziges Lock, keine Dispatch-Queues; Daten überqueren Isolationsgrenzen ausschließlich sichtbar per await. Der alte Ansatz aus Singletons und Streams wäre unter Swifts Sendable-Prüfung schlicht nicht mehr durch den Compiler gekommen, die Migration war so auch ein erzwungenes Architektur-Upgrade."
      },
      {
        subheadline: "Nativ heißt auch: fühlt sich nativ an",
        text: "Hier durfte die Neuauflage sichtbar besser werden: der macOS-Frosted-Glass-Look (NSVisualEffectView für ältere Systeme, ab macOS 26 die native glassEffect-API), animierte SF Symbols für Zustandswechsel und durchgängige Design-Tokens hinter gemeinsamen View-Modifiern. Was Flutter nur imitieren konnte, liefert die Plattform jetzt selbst. Und für die Entwicklung ohne erreichbares Zielsystem gibt es einen vollständigen Mock-Layer als eigenen Actor: identisches Protokoll, realistische Zustandsübergänge, ohne dass echte Stempel-Requests das produktive System erreichen."
      }
    ],
    results: [
      {
        text: "Die native App hat den Flutter-Vorgänger vollständig abgelöst. Nichts Vertrautes brach, und das Neue fühlt sich sofort richtig an:"
      },
      {
        listing: [
          "1:1-Ablösung im produktiven Einsatz: Bestands-Zugangsdaten, Farben, Texte und Verhalten identisch",
          "Verschlüsselungsformat des Vorgängers bit-genau in CryptoKit nachgebaut, kein Migrationsschritt für Nutzerdaten nötig",
          "Durchgängige Actor-Architektur ohne Locks: Thread-Sicherheit garantiert der Compiler, nicht die Konvention",
          "Optik nativ weiterentwickelt: Glass-Look mit Versions-Fallback, animierte SF Symbols, echtes macOS-Bediengefühl",
          "Mock-Layer für vollständige Offline-Entwicklung gegen realistische Zustandsübergänge",
          "Universal-App: als Nice-to-have auf iPhone nachgezogen, jede Ansicht plattformgerecht gestaltet statt bloß gestreckt"
        ]
      },
      {
        text: "Später kam die iPhone-Version dazu, eher als Nice-to-have, weil der Stack es hergab. Statt das Mac-Layout einfach in ein schmales Fenster zu quetschen, ist jede Ansicht eigens fürs iPhone gestaltet, damit sich die App auch dort nativ und nicht wie ein Zugeständnis anfühlt."
      },
      {
        text: "Die eigentliche Erkenntnis ist übertragbar: Eine Migration ist dann gelungen, wenn nichts Vertrautes bricht und sich das Neue sofort richtig anfühlt. Der Blueprint-Ansatz, also alte App als unantastbare Referenz, Parität als Standard und Abweichung nur mit Grund, macht aus einem riskanten Rewrite einen kontrollierten, überprüfbaren Prozess. Und das Projekt markiert für mich eine Zäsur: Der Flutter-Vorgänger entstand noch komplett ohne KI, die Neuauflage KI-nativ. Gleiche App, gleicher Entwickler, zwei Werkzeug-Epochen."
      }
    ],
    technologies: [
      "Swift",
      "SwiftUI",
      "Swift Concurrency (Actors)",
      "CryptoKit",
      "AppKit (NSVisualEffectView)",
      "URLSession",
      "XCTest",
      "Flutter (Referenz)"
    ],
    codeExamples: [
      {
        language: "swift",
        description: "Bit-genaue Kompatibilität zum Flutter-Format: Das gespeicherte Cipher besteht aus drei Hex-Komponenten (Nonce / Ciphertext / Auth-Tag). Die Swift-Seite liest exakt dieses Format mit CryptoKit. Bestandsdaten funktionieren ohne Migrationsschritt weiter.",
        code: `// CryptoService — liest das Cipher-Format des Flutter-Vorgängers
// Format: "<nonceHex>/<cipherHex>/<macHex>"
static func decrypt(_ stored: String, key: SymmetricKey) throws -> String {
    let parts = stored.split(separator: "/").map(String.init)
    guard parts.count == 3,
          let nonceData = Data(hex: parts[0]),   // 12 Bytes IV
          let cipherData = Data(hex: parts[1]),
          let tagData = Data(hex: parts[2])      // 16 Bytes Auth-Tag
    else { throw CryptoError.malformedCipher }

    let sealedBox = try AES.GCM.SealedBox(
        nonce: try AES.GCM.Nonce(data: nonceData),
        ciphertext: cipherData,
        tag: tagData
    )
    let plain = try AES.GCM.open(sealedBox, using: key)
    return String(decoding: plain, as: UTF8.self)
}`
      },
      {
        language: "swift",
        description: "Approachable Concurrency in der Praxis: UI-Code ist per Projekt-Setting MainActor-isoliert, Services sind Actors. Der Compiler erzwingt, dass jeder Grenzübertritt sichtbar per await passiert. Locks und Queues kommen im Projekt nicht vor.",
        code: `// Services sind Actors — serielle Ausführung garantiert der Compiler
actor AutoLogoutScheduler {
    private var task: Task<Void, Never>?

    func schedule(at date: Date, controller: ConnectController) {
        task?.cancel()
        task = Task {
            try? await Task.sleep(until: .now + .seconds(date.timeIntervalSinceNow))
            guard !Task.isCancelled else { return }
            await controller.punch(.logout)   // Actor-Grenze: sichtbar per await
        }
    }
}

// UI bleibt MainActor-isoliert (SWIFT_DEFAULT_ACTOR_ISOLATION = MainActor)
@Observable final class PuncherViewModel {
    private(set) var state: PunchState = .unknown

    func toggle() async {
        state = await controller.togglePunch()  // einzige Brücke zur Service-Welt
    }
}`
      }
    ],
    demoVideo: {
      src: "/assets/video/novatimepuncher_demo.mp4",
      poster: "/assets/img/work/novatimepuncher_demo_poster.webp",
      title: "Im Video",
      caption: "NT Puncher in Aktion: Ein- und Ausstempeln, Pause und Wochenübersicht im Frosted-Glass-Look mit animierten SF Symbols.",
      width: 800,
      height: 1040
    },
    images: [
      {
        src: "/assets/img/work/novatimepuncher_app.webp",
        alt: "NT Puncher Hauptansicht mit Punch-Out-Button, Pausen-Steuerung und Tagesübersicht",
        type: "image",
        title: "Die Stempel-Ansicht",
        caption: "Punch-Out, Pausen-Steuerung und Tagesübersicht im Frosted-Glass-Look."
      },
      {
        src: "/assets/img/work/novatimepuncher_stats.webp",
        alt: "NT Puncher Wochenansicht mit Balkendiagramm pro Tag und Wochensumme",
        type: "image",
        title: "Die Wochenansicht",
        caption: "Kernzeit, Tagesbalken mit 8h- und 10h-Marken, Wochensumme samt Verfall-Warnung oberhalb von 40 Stunden. Die Farbregeln stammen 1:1 aus dem Flutter-Blueprint."
      }
    ]
  }
};

export default novatimepuncher;
